import { Volume2, VolumeX, Headphones } from "lucide-react";

// Stems / groups: auto-group tracks by instrument family and control them as one
// (group gain trim, group mute/solo) — mirrors the "Pistes -> Stems" workflow.

const FAMILIES = [
  { key: "drum", cats: ["drum"] },
  { key: "bass", cats: ["bass"] },
  { key: "synth", cats: ["synth"] },
  { key: "guitar", cats: ["guitar"] },
  { key: "lead", cats: ["lead"] },
  { key: "voice", cats: ["voice_lead"] },
  { key: "choir", cats: ["voice_choir"] },
  { key: "fx", cats: ["brass", "strings", "other"] },
];

export function StemsPanel({ tracks, stemTrims, setStemGain, setStemMute, setStemSolo, catColors, t }) {
  const stems = FAMILIES.map((f) => {
    const members = tracks.filter((x) => f.cats.includes(x.category));
    return { ...f, members };
  }).filter((s) => s.members.length);

  if (!stems.length) return null;

  return (
    <div className="bg-[#0d1017] border border-[#262C3A] rounded-2xl p-3 mb-3" data-testid="stems-panel">
      <div className="flex items-center gap-2 mb-3">
        <Headphones className="h-4 w-4 text-[#06B6D4]" />
        <span className="font-heading font-bold text-white">{t("studio.stemsTitle")}</span>
        <span className="text-xs text-[#64748B]">{t("studio.stemsDesc")}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {stems.map((s) => {
          const ids = s.members.map((m) => m.id);
          const col = catColors[s.cats[0]] || "#64748B";
          const allMuted = s.members.every((m) => m.mute);
          const anySolo = s.members.some((m) => m.solo);
          const trim = stemTrims[s.key] || 0;
          return (
            <div key={s.key} data-testid={`stem-${s.key}`} className="rounded-xl bg-[#131722] border p-2.5" style={{ borderColor: col + "44" }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: col }} />
                  <span className="text-sm font-semibold truncate" style={{ color: col }}>{t(`studio.stem.${s.key}`)}</span>
                  <span className="text-[10px] font-mono text-[#64748B]">×{s.members.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setStemSolo(ids, !anySolo)} data-testid={`stem-solo-${s.key}`}
                    className={`w-6 h-6 rounded text-[10px] font-bold transition-colors ${anySolo ? "bg-[#F59E0B] text-black" : "bg-[#1A1F2C] text-[#94A3B8] hover:bg-[#262C3A]"}`}>S</button>
                  <button onClick={() => setStemMute(ids, !allMuted)} data-testid={`stem-mute-${s.key}`}
                    className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${allMuted ? "bg-[#EF4444] text-white" : "bg-[#1A1F2C] text-[#94A3B8] hover:bg-[#262C3A]"}`}>
                    {allMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="range" min={-12} max={12} step={1} value={trim}
                  onChange={(e) => setStemGain(s.key, ids, parseFloat(e.target.value))}
                  data-testid={`stem-gain-${s.key}`} className="flex-1 h-1.5" style={{ accentColor: col }} />
                <span className="font-mono text-[10px] text-[#94A3B8] w-10 text-right">{trim > 0 ? "+" : ""}{trim} dB</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
